import { z } from "zod";

import { channelSampleSchema } from "../devices/channels";
import { devEuiSchema, telemetryProviderSchema } from "../devices/devices";

/**
 * Provider-independent shape every adapter produces.
 *
 * The domain never sees a The Things Stack envelope, a ChirpStack one or
 * anything else: an adapter translates into this and the rest of the system
 * only knows about it. Adding ChirpStack should mean writing one file.
 */
export const normalizedTelemetrySchema = z.object({
  /**
   * Stable identifier for this uplink, supplied by the provider. It is what
   * makes a retry harmless, so an adapter that cannot find one has to derive
   * a deterministic substitute rather than invent a random value.
   */
  providerEventId: z.string().min(1),

  /** The provider's own device identifier, used to map the uplink. */
  deviceExternalId: z.string().min(1),
  devEui: devEuiSchema.optional(),

  /** When the sensor took the reading. */
  measuredAt: z.iso.datetime(),
  /** When the provider handed it to us. The two differ on a delayed uplink. */
  receivedAt: z.iso.datetime(),

  temperatureC: z.number().optional(),
  humidityPercent: z.number().optional(),
  batteryPercent: z.number().optional(),
  /** Sensor-to-target distance in millimetres, never metres (ADR-060). */
  distanceMm: z.number().optional(),
  /**
   * Whether a person was detected. One bit, never a count (ADR-069).
   *
   * Absent means the uplink said nothing about occupancy, which the pipeline
   * keeps distinct from an empty room all the way to the alert engine.
   */
  occupied: z.boolean().optional(),
  /**
   * Whether conducting liquid reached the spot probe (ADR-077).
   *
   * Absent means the uplink said nothing about leak state, kept distinct
   * from dry all the way to the alert engine.
   */
  leaking: z.boolean().optional(),
  /**
   * Whether the magnetic contact is open (ADR-078).
   *
   * Absent means the uplink said nothing about contact state, kept distinct
   * from closed all the way to the alert engine.
   */
  doorOpen: z.boolean().optional(),
  /**
   * RMS current in amperes (ADR-102).
   *
   * Absent means the uplink said nothing about current. The accumulated
   * ampere-hour counter stays in the raw payload and is not a field here.
   */
  currentA: z.number().optional(),
  /**
   * Controller channel samples from a multi-I/O uplink (ADR-081).
   *
   * Absent or empty for mono-metric sensors. Keys match the Milesight UC300
   * decoder (`gpio_input_1`, `pt100_1`, `modbus_chn_3`, …).
   */
  channels: z.array(channelSampleSchema).optional(),
  rssi: z.number().int().optional(),
  snr: z.number().optional(),
  frameCounter: z.number().int().nonnegative().optional(),

  /**
   * Every gateway that heard this uplink, not just the one that heard it best.
   *
   * LoRaWAN has no attachment between a sensor and a gateway: the device
   * transmits and whatever is in range forwards it, frequently several at
   * once. That is why the list is a list, and why keeping all of it matters —
   * it is the only evidence that a device configured against two gateways for
   * redundancy is genuinely reaching both rather than one twice (ADR-062).
   *
   * `rssi` and `snr` above remain the summary of the strongest reception,
   * which is what belongs on a reading.
   */
  receptions: z
    .array(
      z.object({
        gatewayEui: z.string().min(1).optional(),
        /** The provider's own name for the gateway, for diagnosis only. */
        gatewayExternalId: z.string().min(1).optional(),
        rssi: z.number().int().optional(),
        snr: z.number().optional(),
      }),
    )
    .optional(),

  /**
   * The decoded reading as the provider's codec produced it, kept so a
   * mapping problem can be diagnosed from stored data. Never the full
   * envelope: the parts of the metadata we have a use for are named above,
   * and the rest carries identifiers there is no reason to retain.
   */
  rawPayload: z.record(z.string(), z.unknown()).optional(),
});

export type NormalizedTelemetry = z.infer<typeof normalizedTelemetrySchema>;

/**
 * What became of an uplink.
 *
 * All four are answered with HTTP 200, because all four mean "we have it,
 * stop retrying". Only an unexpected failure returns 5xx, which is the signal
 * the provider's retry mechanism is meant to act on.
 */
export const UPLINK_RESULTS = [
  "ACCEPTED",
  /** Already seen. The event id matched one we stored. */
  "DUPLICATE",
  /** Stored but not applied: no device matched, or the payload made no sense. */
  "DEAD_LETTER",
  /** A well-formed event that carries no reading, such as a join accept. */
  "IGNORED",
] as const;

export const uplinkResultSchema = z.enum(UPLINK_RESULTS);
export type UplinkResult = z.infer<typeof uplinkResultSchema>;

/**
 * Deliberately says almost nothing. The caller is a Network Server, not a
 * user, and an error body that explained *why* a device did not match would
 * let anyone holding the webhook key enumerate which DevEUIs we know.
 */
export const uplinkAckSchema = z.object({
  result: uplinkResultSchema,
  eventId: z.uuid(),
});

export type UplinkAck = z.infer<typeof uplinkAckSchema>;

// =============================================================================
// Simulation
// =============================================================================

/**
 * Generates an uplink as if it came from the Network Server, so the pipeline
 * can be exercised without hardware. Admin-only and rejected in production.
 */
export const simulateUplinkRequestSchema = z.object({
  devEui: devEuiSchema,
  /**
   * Degrees Celsius. The floor stays at -90 so an ambient sensor can still be
   * exercised below the EM500-PT100-T200 probe. The ceiling is 200 so a T200
   * process reading (ovens, pipes) can be simulated (ADR-095).
   */
  temperatureC: z.number().min(-90).max(200).optional(),
  humidityPercent: z.number().min(0).max(100).optional(),
  batteryPercent: z.number().int().min(0).max(100).optional(),
  /**
   * Millimetres, bounded by the EM410-RDL's own 0.3–12 m window so a
   * simulated reading cannot be one the hardware could never produce.
   */
  distanceMm: z.number().min(300).max(12_000).optional(),
  /** Simulated occupancy, so the presence path can be exercised without a sensor. */
  occupied: z.boolean().optional(),
  /** Simulated leak bit for the EM300-SLD path (ADR-077). */
  leaking: z.boolean().optional(),
  /** Simulated door-open bit for the EM300-MCS path (ADR-078). */
  doorOpen: z.boolean().optional(),
  /**
   * Simulated RMS current for the CT103 path (ADR-102).
   *
   * The ceiling sits above the 250 A rating so an over-range reading can be
   * exercised, and below the 0xFFFF sentinel the codec uses for a failed read.
   */
  currentA: z.number().min(0).max(300).optional(),
  /** Simulated controller channel samples for the UC300 path (ADR-081). */
  channels: z.array(channelSampleSchema).optional(),
  rssi: z.number().int().min(-150).max(0).optional(),
  snr: z.number().min(-30).max(30).optional(),
  /** Defaults to now. Accepts a past instant so history can be backfilled. */
  measuredAt: z.iso.datetime().optional(),
});

export type SimulateUplinkRequest = z.infer<typeof simulateUplinkRequestSchema>;

export const simulateUplinkResponseSchema = z.object({
  result: uplinkResultSchema,
  eventId: z.uuid(),
  provider: telemetryProviderSchema,
  devEui: z.string(),
  measuredAt: z.iso.datetime(),
});

export type SimulateUplinkResponse = z.infer<typeof simulateUplinkResponseSchema>;
